import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg, NodeSocketKind, NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import { findTabs, tabGroupError } from './tab-node-helpers';
import { accessor } from 'src/store';
import type { FlowNodeInput } from '../../sockets/FlowNodeInput';

export const goToTabUiNodeSpec: NodeSpec<NodeCfg<'goToTab'>> = {
    nodeCategory: 'tab',
    kindLabel: 'Activate Specific Tab',
    controls: nodeCfg => ({
        tabGroupDetails: defineControlOpts({
            cfg: {
                kind: 'selectTabGroup',
                label: 'Tab Group',
                useSync: true,
            },
            on: {
                'update:value': (tabGroupUpdate, { flowEventEmitter }) => {
                    const tabs = findTabs(typeof tabGroupUpdate === 'string' ? tabGroupUpdate : tabGroupUpdate.id);
                    if (!tabs) {
                        return;
                    }
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                tabGroupDetails: { id: tabGroupUpdate, tabs: tabs.map(tab => ({ tabIndex: tab.idx })) },
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.tabGroupDetails,
        }),
    }),
    cfg: () => ({
        id: getUID(),
        kind: 'goToTab',
        width: 220,
        controlValues: { tabGroupDetails: { id: '', tabs: [] } },
    }),
    inputs: nodeCfg => {
        const inputs: Record<`${number}`, FlowNodeInput.Opts<NodeSocketKindMap['goToTab']['inputs'][`${number}`] & NodeSocketKind>> = {};
        const tabDetails = accessor.appletDesign.getTabDetails(nodeCfg.controlValues.tabGroupDetails.id)?.tabs;

        for (const tabFromNode of nodeCfg.controlValues.tabGroupDetails.tabs) {
            const tabDetail = tabDetails?.find(i => i.idx === tabFromNode.tabIndex);
            inputs[`${tabFromNode.tabIndex}`] = { socket: 'event', label: `Tab ${tabFromNode.tabIndex + 1} (${tabDetail?.name || 'Unknown Tab Name'})` };
        }
        return inputs;
    },
    outputs: () => ({
        next: { socket: 'event', label: 'Next' },
        activeTabIndex: { socket: 'number', label: 'Active Tab Number' },
    }),
    requiredSockets: (node, availableInputSockets) => ({
        required: [],
        conditionallyRequired: [ { kind: 'or', availableOptions: availableInputSockets } ],
    }),
    validator: node => {
        const errors: Record<string, string> = {};
        const tabGroupErrorMessage = tabGroupError(node.controlValues.tabGroupDetails.id);
        if (tabGroupErrorMessage) {
            errors.tabGroupId = tabGroupErrorMessage;
        }
        const tabs = findTabs(node.controlValues.tabGroupDetails.id);
        if (!tabs) {
            errors.tabs = 'No tabs found for the selected tab group';
        } else if (tabs.length !== node.controlValues.tabGroupDetails.tabs.length) {
            errors.tabs = 'Tab selection does not match the number of tabs in the group';
        }
        if (!node.controlValues.tabGroupDetails.tabs.length) {
            errors.tabs = 'At least one tab is required';
        }
        return Object.keys(errors).length ? errors : true;
    },
};
